import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import {
  evictParentPricesAfterDeletion,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidateParentPricesAfterDeletion } from "shared/api/price/modifyPriceCache"
import {
  DeleteResponseTypename,
  MutationSchema,
  MutationTypename
} from "shared/api/types"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { RoomTypename } from "shared/types/Project"
import { DELETE_ROOM } from "shared/types/ProjectMutations"

import { removeProjectChildFromProject } from "entities/project-children/lib/removeProjectChildFromProject"
import { resolveProjectChildParent } from "entities/project-children/lib/resolveProjectChildParent"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import {
  DeleteRoomDocument,
  DeleteRoomMutation,
  DeleteRoomMutationVariables
} from "entities/rooms/graphql/mutations.generated"
import z from "zod"

import { evictRoom } from "../lib/evictRoom"

const DeleteRoomVariablesSchema = z.object({
  id: z.string()
})

export function useDeleteRoom() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Room.DELETE_ROOM.Start"),
      success: t("Request.Room.DELETE_ROOM.Success"),
      failure: t("Request.Room.DELETE_ROOM.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    DeleteRoomMutation,
    DeleteRoomMutationVariables
  >(DeleteRoomDocument)

  const deleteRoom = useEvent(
    async (variables: DeleteRoomMutationVariables, projectID: string) => {
      const parent = await resolveProjectChildParent({
        projectID,
        childID: variables.id,
        cache: apolloCache,
        refetch: () => fetchProject({ variables: { id: projectID } })
      })

      const requestContext = {
        projectID,
        itemID: variables.id,
        itemType: RoomTypename,
        parentID: parent.id,
        parentType: parent.__typename
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DELETE_ROOM
      })

      evictRoom(variables.id, apolloCache)
      invalidateParentPricesAfterDeletion({
        cache: apolloCache,
        context: requestContext
      })

      return callMutation(apolloMutate, {
        variables,
        transform: data => ({ success: data.room.success }),
        schemas: {
          input: DeleteRoomVariablesSchema,
          response: MutationSchema.extend({
            room: z.object({
              __typename: z.literal(DeleteResponseTypename),
              success: z.boolean()
            })
          })
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            room: { __typename: DeleteResponseTypename, success: true }
          },
          update: cache => {
            removeProjectChildFromProject(cache, projectID, variables.id)
          },
          onCompleted: () => {
            evictRoom(variables.id, apolloCache)
            evictParentPricesAfterDeletion({
              cache: apolloCache,
              context: requestContext
            })
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            raiseSuccessToast()
          },
          onError: () => {
            evictProjectPrice(projectID, apolloCache, true)
            fetchProject({ variables: { id: projectID } })
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )
  return [deleteRoom, rest] as [typeof deleteRoom, typeof rest]
}
